// SECURITY: This page is visible to customers. Never display part.cost — it's internal shop data.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Divider,
  Spin,
  Result,
  message,
  Alert,
  Tag,
  Descriptions,
  Modal,
  Input,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  CarOutlined,
  UserOutlined,
  ToolOutlined,
  EditOutlined,
  GiftOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface WorkOrder {
  id: number;
  wo_number: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_company_name?: string;
  customer_phone: string;
  customer_email: string;
  year: number;
  make: string;
  model: string;
  vin: string;
  license_plate: string;
  color?: string;
  line_items: LineItem[];
  pre_approved_budget: number;
  pre_approved_budget_used: number;
}

interface LineItem {
  id: number;
  line_number: number;
  concern: string;
  cause?: string;
  correction?: string;
  customer_approval_status: string;
  labor_hours?: number;
  labor_rate?: number;
  labor_discount_percentage?: number;
  labor_discount_amount?: number;
  labor_total?: number;
  parts_total?: number;
  line_total: number;
  customer_notes?: string;
  approved_at?: string;
  declined_at?: string;
  decline_reason?: string;
  parts: Part[];
  // Diagnosis waiver fields
  is_diagnosis?: boolean;
  is_diagnosis_waived?: boolean;
  diagnosis_waiver_amount?: number;
  applied_diagnosis_credit?: number;
  original_line_total?: number;
  linked_diagnosis_id?: number;
}

interface Part {
  id: number;
  part_name: string;
  part_number?: string;
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  discount_amount?: number;
  total_price: number;
  status: string;
}

const PublicWorkOrderView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [declineModalVisible, setDeclineModalVisible] = useState(false);
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [approveAllModalVisible, setApproveAllModalVisible] = useState(false);
  const [selectedLineItemId, setSelectedLineItemId] = useState<number | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [preApprovedBudget, setPreApprovedBudget] = useState<number>(100);
  const signatureRef = useRef<SignatureCanvas | null>(null);

  useEffect(() => {
    if (token) {
      fetchWorkOrder();
    }
  }, [token]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.get(`${API_BASE_URL}/public/work-orders/${token}`);
      setWorkOrder(response.data);
      if (response.data.pre_approved_budget > 0) {
        setPreApprovedBudget(response.data.pre_approved_budget);
      }
    } catch (err: any) {
      console.error('Error fetching work order:', err);
      setError(err.response?.data?.error || 'Failed to load work order');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLineItem = (lineItemId: number) => {
    setSelectedLineItemId(lineItemId);
    setSignatureModalVisible(true);
  };

  const submitApproval = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      message.warning('Please provide your signature');
      return;
    }

    if (!selectedLineItemId) return;

    try {
      setProcessing(true);
      const signatureData = signatureRef.current.toDataURL();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      await axios.post(`${API_BASE_URL}/public/work-orders/${token}/line-items/${selectedLineItemId}/approve`, {
        signature: signatureData,
        pre_approved_budget: preApprovedBudget,
      });
      message.success('Repair approved successfully');
      setSignatureModalVisible(false);
      setSelectedLineItemId(null);
      await fetchWorkOrder();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to approve repair');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclineLineItem = (lineItemId: number) => {
    setSelectedLineItemId(lineItemId);
    setDeclineReason('');
    setDeclineModalVisible(true);
  };

  const submitDecline = async () => {
    if (!selectedLineItemId) return;

    try {
      setProcessing(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      await axios.post(`${API_BASE_URL}/public/work-orders/${token}/line-items/${selectedLineItemId}/decline`, {
        reason: declineReason || 'No reason provided',
      });
      message.success('Repair declined');
      setDeclineModalVisible(false);
      setSelectedLineItemId(null);
      setDeclineReason('');
      await fetchWorkOrder();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to decline repair');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveAll = () => {
    setApproveAllModalVisible(true);
  };

  const submitApproveAll = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      message.warning('Please provide your signature');
      return;
    }

    try {
      setProcessing(true);
      const signatureData = signatureRef.current.toDataURL();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      await axios.post(`${API_BASE_URL}/public/work-orders/${token}/approve-all`, {
        signature: signatureData,
        pre_approved_budget: preApprovedBudget,
      });
      message.success('All repairs approved successfully');
      setApproveAllModalVisible(false);
      await fetchWorkOrder();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to approve all repairs');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'declined': return 'error';
      case 'pending': return 'warning';
      default: return 'warning';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'declined': return 'Declined';
      case 'pending': return 'Awaiting Your Approval';
      default: return 'Awaiting Your Approval';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Result
          status="error"
          title="Failed to Load Work Order"
          subTitle={error}
        />
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Result
          status="404"
          title="Work Order Not Found"
          subTitle="The work order you're looking for doesn't exist or the link has expired."
        />
      </div>
    );
  }

  const customerName = workOrder.customer_company_name ||
    `${workOrder.customer_first_name} ${workOrder.customer_last_name}`;
  const vehicleInfo = `${workOrder.year} ${workOrder.make} ${workOrder.model}`;

  const pendingLineItems = workOrder.line_items.filter(item =>
    item.customer_approval_status === 'pending'
  );

  const approvedLineItems = workOrder.line_items.filter(item =>
    item.customer_approval_status === 'approved'
  );

  const totalAmount = pendingLineItems.reduce((sum, item) => sum + item.line_total, 0);

  // Calculate total savings (including diagnosis waivers) for approved items
  const totalSavings = pendingLineItems.reduce((sum, item) => {
    const laborDiscount = item.labor_discount_amount || 0;
    const partsDiscount = (item.parts || []).reduce((pSum, part) => pSum + (part.discount_amount || 0), 0);
    const diagnosisWaiver = item.is_diagnosis_waived ? (item.diagnosis_waiver_amount || 0) : 0;
    return sum + laborDiscount + partsDiscount + diagnosisWaiver;
  }, 0);

  // Calculate approved total and ALL savings (labor, parts, diagnosis waivers)
  const approvedTotal = approvedLineItems.reduce((sum, item) => sum + item.line_total, 0);

  const approvedSavings = approvedLineItems.reduce((sum, item) => {
    let itemSavings = 0;

    // Labor discount
    itemSavings += item.labor_discount_amount || 0;

    // Parts discounts
    const partsDiscount = (item.parts || []).reduce((pSum, part) =>
      pSum + (part.discount_amount || 0), 0);
    itemSavings += partsDiscount;

    // Diagnosis waiver (if this was a diagnosis that got waived)
    if (item.is_diagnosis_waived) {
      itemSavings += item.diagnosis_waiver_amount || 0;
    }

    return sum + itemSavings;
  }, 0);

  const approvedOriginalTotal = approvedTotal + approvedSavings;

  return (
    <div style={{ backgroundColor: '#f3f4f6', minHeight: '100vh', padding: window.innerWidth < 768 ? '12px' : '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <WarningOutlined style={{ fontSize: '48px', color: '#f59e0b', marginBottom: '16px' }} />
            <Title level={2} style={{ margin: 0 }}>Additional Repairs Required</Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>Work Order #{workOrder.wo_number}</Text>
          </div>

          {pendingLineItems.length > 0 && (
            <Alert
              message="Action Required"
              description={`We discovered ${pendingLineItems.length} additional issue${pendingLineItems.length !== 1 ? 's' : ''} that require${pendingLineItems.length === 1 ? 's' : ''} your approval before we can proceed with the repairs.`}
              type="warning"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}
        </Card>

        <Row gutter={[24, 24]}>
          {/* Main Content */}
          <Col xs={24} lg={16}>
            {/* Line Items */}
            <Card title={`Repairs Requiring Approval (${pendingLineItems.length})`}>
              {workOrder.line_items.map((lineItem) => {
                const isPending = lineItem.customer_approval_status === 'pending';

                return (
                  <Card
                    key={lineItem.id}
                    type="inner"
                    title={
                      <Space>
                        <Text strong>Line {lineItem.line_number}</Text>
                        <Tag color={getStatusColor(lineItem.customer_approval_status)}>
                          {getStatusText(lineItem.customer_approval_status)}
                        </Tag>
                        {lineItem.is_diagnosis_waived && (
                          <Tag icon={<GiftOutlined />} color="success" style={{ fontSize: '13px', fontWeight: 600 }}>
                            FREE
                          </Tag>
                        )}
                        {lineItem.is_diagnosis && !lineItem.is_diagnosis_waived && (
                          <Tag color="gold">DIAGNOSIS</Tag>
                        )}
                      </Space>
                    }
                    style={{
                      marginBottom: '16px',
                      backgroundColor: lineItem.is_diagnosis_waived ? '#f0fdf4' : 'white',
                      borderLeft: lineItem.is_diagnosis_waived ? '4px solid #22c55e' : undefined
                    }}
                    extra={
                      isPending && (
                        <Space>
                          <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleApproveLineItem(lineItem.id)}
                            loading={processing}
                          >
                            Approve
                          </Button>
                          <Button
                            danger
                            icon={<CloseCircleOutlined />}
                            onClick={() => handleDeclineLineItem(lineItem.id)}
                            loading={processing}
                          >
                            Decline
                          </Button>
                        </Space>
                      )
                    }
                  >
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Concern (What you reported)">
                        <Text>{lineItem.concern}</Text>
                      </Descriptions.Item>
                      {lineItem.cause && (
                        <Descriptions.Item label="Cause (What we found)">
                          <Text>{lineItem.cause}</Text>
                        </Descriptions.Item>
                      )}
                      {lineItem.correction && (
                        <Descriptions.Item label="Correction (What we recommend)">
                          <Text>{lineItem.correction}</Text>
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    {/* Show diagnosis waiver notice for repairs linked to diagnosis */}
                    {!lineItem.is_diagnosis && lineItem.linked_diagnosis_id && isPending && (() => {
                      const linkedDiagnosis = workOrder.line_items.find(item => item.id === lineItem.linked_diagnosis_id);
                      if (linkedDiagnosis) {
                        return (
                          <Alert
                            message={
                              <div>
                                <GiftOutlined style={{ marginRight: 8, fontSize: 16 }} />
                                <strong>Good News! If you approve this repair:</strong>
                              </div>
                            }
                            description={
                              <div>
                                <Text>The diagnosis fee for "<strong>{linkedDiagnosis.concern}</strong>" (${linkedDiagnosis.line_total.toFixed(2)}) will automatically be waived and made <strong style={{ color: '#22c55e' }}>FREE</strong>!</Text>
                                <Divider style={{ margin: '8px 0' }} />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  💡 You won't pay for the diagnosis when you approve the repair. The diagnosis fee will be removed automatically.
                                </Text>
                              </div>
                            }
                            type="success"
                            showIcon={false}
                            style={{
                              marginTop: '16px',
                              marginBottom: '16px',
                              backgroundColor: '#f0fdf4',
                              border: '2px solid #22c55e'
                            }}
                          />
                        );
                      }
                      return null;
                    })()}

                    {lineItem.parts && lineItem.parts.length > 0 && (
                      <>
                        <Divider />
                        <Title level={5}>Parts Required:</Title>
                        {lineItem.parts.map((part, idx) => {
                          const partSubtotal = part.quantity * part.unit_price;
                          const hasDiscount = (part.discount_amount || 0) > 0;
                          return (
                            <div key={idx} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                              <Row justify="space-between">
                                <Col>
                                  <Text strong>{part.part_name}</Text>
                                  {part.part_number && <Text type="secondary"> (#{part.part_number})</Text>}
                                  <br />
                                  <Text type="secondary">Qty: {part.quantity} × ${part.unit_price.toFixed(2)}</Text>
                                  {hasDiscount && (
                                    <>
                                      <br />
                                      <Text type="success" style={{ fontSize: '12px' }}>
                                        Discount: {part.discount_percentage}% off (Save ${part.discount_amount?.toFixed(2)})
                                      </Text>
                                    </>
                                  )}
                                </Col>
                                <Col style={{ textAlign: 'right' }}>
                                  {hasDiscount && (
                                    <>
                                      <Text delete type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                        ${partSubtotal.toFixed(2)}
                                      </Text>
                                    </>
                                  )}
                                  <Text strong style={{ color: '#22c55e' }}>${(hasDiscount && (part as any).discounted_price ? (part as any).discounted_price : part.total_price).toFixed(2)}</Text>
                                </Col>
                              </Row>
                            </div>
                          );
                        })}
                      </>
                    )}

                    <Divider />
                    {(() => {
                      const laborSubtotal = (lineItem.labor_hours || 0) * (lineItem.labor_rate || 0);
                      const laborDiscount = lineItem.labor_discount_amount || 0;
                      const partsSubtotal = (lineItem.parts || []).reduce((sum, part) => sum + (part.quantity * part.unit_price), 0);
                      const partsDiscount = (lineItem.parts || []).reduce((sum, part) => sum + (part.discount_amount || 0), 0);
                      const totalDiscount = laborDiscount + partsDiscount;
                      const subtotal = laborSubtotal + partsSubtotal;
                      const originalTotal = lineItem.original_line_total || lineItem.line_total;

                      // Total savings includes diagnosis waiver if applicable
                      const diagnosisWaiverAmount = lineItem.is_diagnosis_waived ? (lineItem.diagnosis_waiver_amount || 0) : 0;
                      const totalSavings = totalDiscount + diagnosisWaiverAmount;

                      return (
                        <>
                          {lineItem.is_diagnosis_waived && (
                            <Alert
                              message={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <GiftOutlined style={{ marginRight: 8, fontSize: 18 }} />
                                    <strong style={{ fontSize: '15px' }}>Diagnosis Fee Waived!</strong>
                                  </div>
                                  <div>
                                    <Text delete type="secondary" style={{ marginRight: 8 }}>
                                      ${diagnosisWaiverAmount.toFixed(2)}
                                    </Text>
                                    <Text strong style={{ fontSize: '18px', color: '#22c55e' }}>FREE</Text>
                                  </div>
                                </div>
                              }
                              description="The shop has waived your diagnosis fee - you won't be charged for this service!"
                              type="success"
                              showIcon={false}
                              style={{ marginBottom: '12px', border: '2px solid #22c55e' }}
                            />
                          )}

                          {lineItem.labor_hours && lineItem.labor_rate && (
                            <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                              <Col>
                                <Text type="secondary">
                                  Labor: {lineItem.labor_hours}h @ ${lineItem.labor_rate}/hr
                                  {laborDiscount > 0 && (
                                    <Text type="success" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                      (-{lineItem.labor_discount_percentage}%)
                                    </Text>
                                  )}
                                </Text>
                              </Col>
                              <Col>
                                {laborDiscount > 0 && (
                                  <>
                                    <Text delete type="secondary" style={{ fontSize: '12px', marginRight: '8px' }}>
                                      ${laborSubtotal.toFixed(2)}
                                    </Text>
                                  </>
                                )}
                                <Text strong>${lineItem.labor_total?.toFixed(2)}</Text>
                              </Col>
                            </Row>
                          )}

                          {/* Parts Total Row */}
                          {lineItem.parts && lineItem.parts.length > 0 && (
                            <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                              <Col>
                                <Text type="secondary">
                                  Parts: {lineItem.parts.length} item{lineItem.parts.length !== 1 ? 's' : ''}
                                  {partsDiscount > 0 && (
                                    <Text type="success" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                      (discounts applied)
                                    </Text>
                                  )}
                                </Text>
                              </Col>
                              <Col>
                                {partsDiscount > 0 && (
                                  <>
                                    <Text delete type="secondary" style={{ fontSize: '12px', marginRight: '8px' }}>
                                      ${partsSubtotal.toFixed(2)}
                                    </Text>
                                  </>
                                )}
                                <Text strong>${lineItem.parts_total?.toFixed(2)}</Text>
                              </Col>
                            </Row>
                          )}

                          {totalSavings > 0 && (
                            <Alert
                              message={`💰 You're saving $${totalSavings.toFixed(2)} on this ${lineItem.is_diagnosis ? 'diagnosis' : 'repair'}!`}
                              type="success"
                              showIcon
                              style={{ marginBottom: '12px' }}
                            />
                          )}
                          <Row justify="space-between" align="middle" style={{
                            backgroundColor: '#f0fdf4',
                            padding: '12px',
                            borderRadius: '6px',
                            borderLeft: '4px solid #22c55e'
                          }}>
                            <Col>
                              <Space direction="vertical" size={0}>
                                <Text strong style={{ fontSize: '16px' }}>Line Total:</Text>
                                {totalSavings > 0 && (
                                  <Text delete type="secondary" style={{ fontSize: '12px' }}>
                                    Was: ${originalTotal.toFixed(2)}
                                  </Text>
                                )}
                              </Space>
                            </Col>
                            <Col>
                              <Text strong style={{ fontSize: '22px', color: '#22c55e' }}>
                                ${lineItem.line_total.toFixed(2)}
                              </Text>
                            </Col>
                          </Row>
                        </>
                      );
                    })()}


                    {lineItem.customer_approval_status === 'declined' && lineItem.decline_reason && (
                      <Alert
                        message="Declined"
                        description={`Reason: ${lineItem.decline_reason}`}
                        type="error"
                        showIcon
                        style={{ marginTop: '16px' }}
                      />
                    )}

                    {lineItem.customer_approval_status === 'approved' && lineItem.approved_at && (
                      <Alert
                        message="Approved"
                        description={`Approved on ${new Date(lineItem.approved_at).toLocaleString()}`}
                        type="success"
                        showIcon
                        style={{ marginTop: '16px' }}
                      />
                    )}
                  </Card>
                );
              })}
            </Card>
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            {/* Summary */}
            {/* Approved Repairs Summary - Show AFTER customer approves */}
            {approvedLineItems.length > 0 && (() => {
              // Calculate breakdown of savings
              const laborDiscounts = approvedLineItems.reduce((sum, item) =>
                sum + (item.labor_discount_amount || 0), 0);
              const partsDiscounts = approvedLineItems.reduce((sum, item) =>
                sum + (item.parts || []).reduce((pSum, part) => pSum + (part.discount_amount || 0), 0), 0);
              const diagnosisWaivers = approvedLineItems.reduce((sum, item) =>
                sum + (item.is_diagnosis_waived ? (item.diagnosis_waiver_amount || 0) : 0), 0);

              return (
                <Card
                  title="Approved Repairs"
                  style={{ marginBottom: '24px' }}
                >
                  <div style={{ padding: '16px' }}>
                    {approvedSavings > 0 ? (
                      <>
                        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Original Total:</Text>
                          <Text delete type="secondary" style={{ fontSize: '18px' }}>
                            ${approvedOriginalTotal.toFixed(2)}
                          </Text>
                        </div>

                        {/* Savings Breakdown */}
                        <Alert
                          message={
                            <div>
                              <strong>Total Savings Breakdown:</strong>
                            </div>
                          }
                          description={
                            <div style={{ fontSize: '12px' }}>
                              {laborDiscounts > 0 && (
                                <div style={{ marginBottom: '4px' }}>
                                  ✓ Labor discounts: <Text type="success" strong>${laborDiscounts.toFixed(2)}</Text>
                                </div>
                              )}
                              {partsDiscounts > 0 && (
                                <div style={{ marginBottom: '4px' }}>
                                  ✓ Parts discounts: <Text type="success" strong>${partsDiscounts.toFixed(2)}</Text>
                                </div>
                              )}
                              {diagnosisWaivers > 0 && (
                                <div style={{ marginBottom: '4px' }}>
                                  ✓ Diagnosis fees waived: <Text type="success" strong>${diagnosisWaivers.toFixed(2)}</Text>
                                </div>
                              )}
                              <Divider style={{ margin: '8px 0' }} />
                              <div>
                                <strong>Total You Saved: <Text type="success" style={{ fontSize: '14px' }}>${approvedSavings.toFixed(2)}</Text></strong>
                              </div>
                            </div>
                          }
                          type="success"
                          showIcon={false}
                          style={{ marginBottom: '16px', backgroundColor: '#f0fdf4', border: '2px solid #22c55e' }}
                        />

                        <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '3px solid #22c55e' }}>
                          <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>You're Paying:</Text>
                          <Title level={2} style={{ color: '#22c55e', margin: 0 }}>
                            ${approvedTotal.toFixed(2)}
                          </Title>
                          <Text type="success" style={{ fontSize: '12px', display: 'block', marginTop: '4px', fontWeight: 600 }}>
                            💰 Total Saved: ${approvedSavings.toFixed(2)}
                          </Text>
                        </div>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary">Approved Total:</Text>
                        <Title level={2} style={{ color: '#22c55e', margin: '8px 0' }}>
                          ${approvedTotal.toFixed(2)}
                        </Title>
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', textAlign: 'center', marginTop: '12px' }}>
                      {approvedLineItems.length} item{approvedLineItems.length !== 1 ? 's' : ''} approved
                    </Text>
                  </div>
                </Card>
              );
            })()}

            {/* Pending Repairs Summary */}
            {pendingLineItems.length > 0 && (
              <Card
                title="Pending Approval"
                style={{ marginBottom: '24px' }}
              >
                <div style={{ padding: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">Additional Repairs Total:</Text>
                    <Title level={2} style={{ color: '#f59e0b', margin: '8px 0' }}>
                      ${totalAmount.toFixed(2)}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      {pendingLineItems.length} item{pendingLineItems.length !== 1 ? 's' : ''} awaiting approval
                    </Text>
                  </div>

                  <Divider />

                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleApproveAll}
                    loading={processing}
                    block
                    style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                  >
                    Approve All Repairs
                  </Button>
                </div>
              </Card>
            )}

            {/* Pre-Approved Budget Info */}
            {workOrder.pre_approved_budget > 0 && (
              <Card
                title="Pre-Approved Budget"
                style={{ marginBottom: '24px' }}
              >
                <div style={{ textAlign: 'center', padding: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                    Budget for small additional repairs:
                  </Text>
                  <Title level={3} style={{ color: '#1890ff', margin: '4px 0' }}>
                    ${(workOrder.pre_approved_budget - (workOrder.pre_approved_budget_used || 0)).toFixed(2)} remaining
                  </Title>
                  {(workOrder.pre_approved_budget_used || 0) > 0 && (
                    <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                      ${(workOrder.pre_approved_budget_used || 0).toFixed(2)} of ${workOrder.pre_approved_budget.toFixed(2)} used
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '8px' }}>
                    Items within this budget are auto-approved so we don't need to call you for every small item.
                  </Text>
                </div>
              </Card>
            )}

            {/* Customer Info */}
            <Card
              title="Customer Information"
              style={{ marginBottom: '24px' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Name</Text>
                  <Text strong>{customerName}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Phone</Text>
                  <Text>{workOrder.customer_phone}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Email</Text>
                  <Text>{workOrder.customer_email}</Text>
                </div>
              </Space>
            </Card>

            {/* Vehicle Info */}
            <Card title="Vehicle Information">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Vehicle</Text>
                  <Text strong>{vehicleInfo}</Text>
                </div>
                {workOrder.license_plate && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>License Plate</Text>
                    <Text>{workOrder.license_plate}</Text>
                  </div>
                )}
                {workOrder.vin && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>VIN</Text>
                    <Text style={{ fontSize: '12px' }}>{workOrder.vin}</Text>
                  </div>
                )}
                {workOrder.color && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Color</Text>
                    <Text>{workOrder.color}</Text>
                  </div>
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Signature Modal - Single Line Item */}
        <Modal
          title="Sign to Approve Repair"
          open={signatureModalVisible}
          onOk={submitApproval}
          onCancel={() => {
            setSignatureModalVisible(false);
            setSelectedLineItemId(null);
            if (signatureRef.current) {
              signatureRef.current.clear();
            }
          }}
          okText="Approve"
          okButtonProps={{ loading: processing, icon: <CheckCircleOutlined /> }}
          width={600}
          centered
        >
          <div style={{ marginBottom: '16px' }}>
            <Alert
              message="Signature Required"
              description="Please sign below to approve this repair. Your signature confirms that you authorize us to proceed with this work."
              type="info"
              showIcon
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Sign Here:</Text>
          </div>
          <div style={{ border: '2px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fafafa' }}>
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 550,
                height: 200,
                style: { width: '100%', height: '200px' }
              }}
            />
          </div>
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <Button
              size="small"
              onClick={() => signatureRef.current?.clear()}
              icon={<EditOutlined />}
            >
              Clear Signature
            </Button>
          </div>
        </Modal>

        {/* Signature Modal - Approve All */}
        <Modal
          title="Sign to Approve All Repairs"
          open={approveAllModalVisible}
          onOk={submitApproveAll}
          onCancel={() => {
            setApproveAllModalVisible(false);
            if (signatureRef.current) {
              signatureRef.current.clear();
            }
          }}
          okText="Approve All"
          okButtonProps={{ loading: processing, icon: <CheckCircleOutlined /> }}
          width={600}
          centered
        >
          <div style={{ marginBottom: '16px' }}>
            <Alert
              message="Signature Required"
              description="Please sign below to approve all pending repairs. Your signature confirms that you authorize us to proceed with all the work listed."
              type="warning"
              showIcon
            />
          </div>

          {/* Pre-Approved Budget */}
          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#f0f9ff',
            border: '1px solid #91d5ff',
            borderRadius: 8,
          }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Pre-Approved Repair Budget (Optional)</Text>
            </div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
              If we find small additional items during repair (clamp, bolt, fluid top-off),
              we can handle them without calling you — up to this amount.
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>$</span>
              <Input
                type="number"
                value={preApprovedBudget}
                onChange={(e) => setPreApprovedBudget(Math.max(0, Math.min(10000, parseFloat(e.target.value) || 0)))}
                style={{ width: 120, fontSize: 16, fontWeight: 600 }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                {[50, 100, 200].map(amt => (
                  <Button
                    key={amt}
                    size="small"
                    type={preApprovedBudget === amt ? 'primary' : 'default'}
                    onClick={() => setPreApprovedBudget(amt)}
                    style={{ fontSize: 12 }}
                  >
                    ${amt}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <Text strong>Sign Here:</Text>
          </div>
          <div style={{ border: '2px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fafafa' }}>
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 550,
                height: 200,
                style: { width: '100%', height: '200px' }
              }}
            />
          </div>
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <Button
              size="small"
              onClick={() => signatureRef.current?.clear()}
              icon={<EditOutlined />}
            >
              Clear Signature
            </Button>
          </div>
        </Modal>

        {/* Decline Modal */}
        <Modal
          title="Decline Repair"
          open={declineModalVisible}
          onOk={submitDecline}
          onCancel={() => {
            setDeclineModalVisible(false);
            setSelectedLineItemId(null);
            setDeclineReason('');
          }}
          okText="Submit"
          okButtonProps={{ loading: processing, danger: true }}
          centered
        >
          <Text>Please provide a reason for declining this repair:</Text>
          <TextArea
            rows={4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter your reason here..."
            style={{ marginTop: '12px' }}
          />
        </Modal>
      </div>
    </div>
  );
};

export default PublicWorkOrderView;
